import { useCallback, useEffect, useState } from "react";
import { Search, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PageHeader, Section } from "../components/AiHtml";
import { ReportButton } from "../components/ReportButton";
import { AdminOverview } from "../components/AdminOverview";
import { useAuth } from "../context/AuthContext";
import { money } from "../components/BuyPlanDialog";
import { api, handleErr } from "../lib/api";

const fmt = (d) => new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const statusCls = { pending: "text-amber-400 bg-amber-500/10", "awaiting payment": "text-slate-400 bg-slate-500/10", approved: "text-emerald-400 bg-emerald-500/10", rejected: "text-red-400 bg-red-500/10", active: "text-emerald-400 bg-emerald-500/10", disabled: "text-red-400 bg-red-500/10" };
const Badge = ({ s }) => <span className={`px-2 py-0.5 rounded-full text-xs ${statusCls[s] || ""}`}>{s}</span>;

const PaymentsTable = ({ items, onAction }) => (
  <div className="overflow-x-auto"><table className="w-full text-sm min-w-[720px]">
    <thead><tr className="text-left text-xs font-mono uppercase text-slate-500"><th className="py-2">Date</th><th>User</th><th>Plan</th><th>Amount</th><th>Payment ID</th><th>Status</th><th /></tr></thead>
    <tbody>{items.map((p) => (
      <tr key={p.requestId} className="border-t border-slate-800" data-testid={`payment-row-${p.requestId}`}>
        <td className="py-2.5 text-xs text-slate-400">{fmt(p.date)}</td><td>{p.email}</td><td>{p.plan}</td><td className="font-mono">{money(p.amount, p.currency || "INR")}</td><td className="font-mono text-xs">{p.utr || p.orderId || "—"}</td>
        <td><Badge s={p.status === "created" ? "awaiting payment" : p.status} /></td>
        <td className="text-right whitespace-nowrap">{["pending", "created"].includes(p.status) && onAction && (<>
          <Button size="sm" data-testid={`payment-approve-${p.requestId}`} title={p.method === "razorpay" ? "Check Razorpay & credit if paid" : "Approve"} onClick={() => onAction(p.requestId, "approve")} className="h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 mr-1">{p.method === "razorpay" ? <span className="text-[11px] px-1">Check paid</span> : <Check size={13} />}</Button>
          <Button size="sm" variant="destructive" data-testid={`payment-reject-${p.requestId}`} onClick={() => onAction(p.requestId, "reject")} className="h-7 rounded-full"><X size={13} /></Button></>)}</td>
      </tr>))}
      {items.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-500">No payments.</td></tr>}
    </tbody></table></div>
);

const UserDetail = ({ uid, onChanged, onPayAction }) => {
  const { impersonate } = useAuth();
  const [d, setD] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get(`/admin/users/${uid}`).then((r) => setD(r.data)), [uid]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const u = d.user;
  const adjust = async (sign) => {
    try {
      await api.post(`/admin/users/${uid}/credits`, { amount: sign * Math.abs(Number(amount)), reason });
      toast.success("Credits updated");
      setAmount(""); setReason(""); load(); onChanged();
    } catch (e) { handleErr(e); }
  };
  const toggle = async () => {
    try {
      await api.post(`/admin/users/${uid}/status`, { status: u.status === "active" ? "disabled" : "active" });
      toast.success("Status updated"); load(); onChanged();
    } catch (e) { handleErr(e); }
  };
  return (
    <div className="space-y-6" data-testid="admin-user-detail">
      <Section title={u.email} right={<Badge s={u.status} />}>
        <div className="grid sm:grid-cols-4 gap-4 text-sm">
          <div><p className="label-mono">Name</p><p>{u.name}</p></div>
          <div><p className="label-mono">Credits</p><p data-testid="admin-user-credits" className="font-display text-2xl font-bold text-orange-400">{u.credits}</p></div>
          <div><p className="label-mono">Plan</p><p>{u.plan}{u.billing ? ` · ${u.billing}` : ""}</p>{u.planExpiresAt && <p className="text-xs text-slate-500">renews {new Date(u.planExpiresAt).toLocaleDateString()}</p>}</div>
          <div><p className="label-mono">Joined</p><p className="text-xs">{fmt(u.createdAt)}</p></div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-5">
          <Input data-testid="admin-credit-amount" type="number" placeholder="Credits" value={amount} onChange={(e) => setAmount(e.target.value)} className="sm:w-32" />
          <Input data-testid="admin-credit-reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button data-testid="admin-credit-add" disabled={!amount || reason.length < 2} onClick={() => adjust(1)} className="rounded-full">Add</Button>
          <Button data-testid="admin-credit-deduct" variant="outline" disabled={!amount || reason.length < 2} onClick={() => adjust(-1)} className="rounded-full">Deduct</Button>
          <Button data-testid="admin-open-account" variant="outline" onClick={() => impersonate(uid).catch(handleErr)} className="rounded-full">Open account</Button>
          <Button data-testid="admin-toggle-status" variant={u.status === "active" ? "destructive" : "default"} onClick={toggle} className="rounded-full">{u.status === "active" ? "Disable account" : "Activate account"}</Button>
        </div>
      </Section>
      <Section title="Monthly client reports" testId="admin-reports">
        {d.brands.length === 0 ? <p className="text-sm text-slate-500">No brands for this user.</p> : (
          <div className="space-y-3">{d.brands.map((b) => (
            <div key={b.brandId} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-3 last:border-0">
              <div><p className="text-sm font-medium">{b.businessName}</p><p className="text-xs text-slate-500">{b.city} · {b.website}</p></div>
              <ReportButton path="/admin/reports/monthly" params={{ uid, brandId: b.brandId }} testId={`admin-report-${b.brandId}`} />
            </div>))}
          </div>
        )}
      </Section>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title={`Usage (${d.usage.length})`}><ul className="space-y-1.5 text-sm max-h-64 overflow-auto scroll-thin">{d.usage.map((x) => <li key={x.logId} className="flex justify-between"><span className="capitalize">{x.task.replace("_", " ")}</span><span className="font-mono text-xs text-slate-500">-{x.cost} · {fmt(x.date)}</span></li>)}</ul></Section>
        <Section title="Credit ledger"><ul className="space-y-1.5 text-sm max-h-64 overflow-auto scroll-thin" data-testid="admin-ledger">{d.ledger.map((x) => <li key={x.entryId} className="flex justify-between gap-2"><span className="truncate text-slate-400">{x.reason} <span className="text-slate-600">· {x.adminEmail}</span></span><span className={`font-mono text-xs ${x.creditsAdded >= 0 ? "text-emerald-400" : "text-red-400"}`}>{x.creditsAdded}</span></li>)}</ul></Section>
      </div>
      <Section title="Payments"><PaymentsTable items={d.payments} onAction={async (id, a) => { await onPayAction(id, a); load(); }} /></Section>
    </div>
  );
};

export default function Admin() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null);
  const [pays, setPays] = useState([]);
  const [stats, setStats] = useState({});
  const loadUsers = useCallback(() => api.get("/admin/users", { params: { q } }).then((r) => setUsers(r.data)), [q]);
  const loadPays = () => { api.get("/admin/payments").then((r) => setPays(r.data)); api.get("/admin/stats").then((r) => setStats(r.data)); };
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadPays(); }, []);
  const payAction = async (id, action) => {
    try { await api.post(`/admin/payments/${id}/${action}`); toast.success(`Payment ${action}d`); loadPays(); loadUsers(); } catch (e) { handleErr(e); }
  };

  return (
    <div className="space-y-6">
      <PageHeader label="Admin" title="Admin panel" desc="Manage users, credits, account status and payment approvals." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Users", stats.users], ["Pending payments", stats.pending], ["Disabled", stats.disabled], ["Tasks run", stats.tasks]].map(([l, v]) => <div key={l} className="card-n p-4"><p className="label-mono">{l}</p><p className="font-display text-3xl font-bold mt-1">{v ?? "—"}</p></div>)}
      </div>
      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="overview" data-testid="admin-tab-overview" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Users</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">Payments {stats.pending ? `(${stats.pending})` : ""}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><AdminOverview /></TabsContent>
        <TabsContent value="users" className="mt-4">
          <div className="grid lg:grid-cols-[340px_1fr] gap-6">
            <div className="card-n p-3">
              <div className="relative mb-3"><Search size={14} className="absolute left-3 top-3 text-slate-500" /><Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email" className="pl-8" /></div>
              <div className="space-y-1 max-h-[60vh] overflow-auto scroll-thin">
                {users.map((u) => (
                  <button key={u.uid} data-testid={`admin-user-${u.email}`} onClick={() => setSel(u.uid)} className={`w-full text-left px-3 py-2 rounded-lg text-sm flex justify-between gap-2 ${sel === u.uid ? "bg-orange-500/10 text-orange-300" : "hover:bg-slate-800/60"}`}>
                    <span className="truncate">{u.email}</span><span className="font-mono text-xs text-slate-500">{u.credits}</span>
                  </button>
                ))}
              </div>
            </div>
            {sel ? <UserDetail key={sel} uid={sel} onChanged={() => { loadUsers(); loadPays(); }} onPayAction={payAction} /> : <p className="text-slate-500 text-sm">Select a user to view details.</p>}
          </div>
        </TabsContent>
        <TabsContent value="payments" className="mt-4"><Section title="All payment requests"><PaymentsTable items={pays} onAction={payAction} /></Section></TabsContent>
      </Tabs>
    </div>
  );
}
